import { supabase } from '../../lib/supabase.js';
import { RepositoryError } from '../errors/RepositoryError.js';
import { handleWarn } from '../../utils/errorHandling.js';

const table = (name) => supabase.from(name);

const isMissing = (error) =>
  error?.message?.toLowerCase().includes('schema cache') ||
  error?.details?.toLowerCase?.().includes('does not exist');

const handle = (res, context) => {
  if (res.error) {
    if (isMissing(res.error)) return [];
    throw new RepositoryError(`Social ${context}: ${res.error.message}`, res.error);
  }
  return res.data || [];
};

// =====================================================
// USER PROFILES
// =====================================================

/**
 * Get a user profile by user ID
 * @param {string} userId - User ID
 * @returns {Promise<Object|null>} User profile or null
 */
export async function getUserProfile(userId) {
  if (!userId) return null;

  const res = await table('user_profiles')
    .select('*')
    .eq('user_id', userId)
    .single();

  if (res.error) {
    if (isMissing(res.error) || res.error.code === 'PGRST116') return null;
    throw new RepositoryError(`getUserProfile: ${res.error.message}`, res.error);
  }

  return res.data;
}

/**
 * Get a user profile by username
 * @param {string} username - Username
 * @returns {Promise<Object|null>} User profile or null
 */
export async function getUserProfileByUsername(username) {
  if (!username) return null;

  const res = await table('user_profiles')
    .select('*')
    .eq('username', username)
    .single();

  if (res.error) {
    if (isMissing(res.error) || res.error.code === 'PGRST116') return null;
    throw new RepositoryError(`getUserProfileByUsername: ${res.error.message}`, res.error);
  }

  return res.data;
}

/**
 * Create or update a user profile
 * @param {Object} profile - Profile data
 * @returns {Promise<Object>} Saved profile
 */
export async function upsertUserProfile(profile) {
  const res = await table('user_profiles')
    .upsert(profile, { onConflict: 'user_id' })
    .select()
    .single();

  if (res.error) {
    throw new RepositoryError(`upsertUserProfile: ${res.error.message}`, res.error);
  }

  return res.data;
}

/**
 * Update user profile stats (workouts, PRs)
 * @param {string} userId - User ID
 * @param {Object} stats - Stats to update
 * @returns {Promise<Object>} Updated profile
 */
export async function updateUserStats(userId, stats) {
  const res = await table('user_profiles')
    .update(stats)
    .eq('user_id', userId)
    .select()
    .single();

  return handle(res, 'updateUserStats');
}

/**
 * Search users by username or display name
 * @param {string} query - Search query
 * @param {number} limit - Max results (default: 20)
 * @returns {Promise<Array>} Matching user profiles
 */
export async function searchUsers(query, limit = 20) {
  if (!query) return [];

  const searchTerm = `%${query.toLowerCase()}%`;

  const res = await supabase.rpc('search_users', {
    search_query: searchTerm,
    result_limit: limit
  });

  // Fallback if RPC doesn't exist yet
  if (res.error && res.error.code === '42883') {
    const fallbackRes = await table('user_profiles')
      .select('*')
      .or(`username.ilike.${searchTerm},display_name.ilike.${searchTerm}`)
      .limit(limit);

    return handle(fallbackRes, 'searchUsers');
  }

  return handle(res, 'searchUsers');
}

// =====================================================
// FOLLOWS
// =====================================================

/**
 * Follow a user
 * @param {string} followerId - User who is following
 * @param {string} followingId - User being followed
 * @returns {Promise<Object>} Follow record
 */
export async function followUser(followerId, followingId) {
  if (!followerId || !followingId) {
    throw new RepositoryError('followUser: followerId and followingId required');
  }

  if (followerId === followingId) {
    throw new RepositoryError('followUser: Cannot follow yourself');
  }

  const res = await table('follows')
    .insert({ follower_id: followerId, following_id: followingId })
    .select()
    .single();

  if (res.error) {
    throw new RepositoryError(`followUser: ${res.error.message}`, res.error);
  }

  return res.data;
}

/**
 * Unfollow a user
 * @param {string} followerId - User who is unfollowing
 * @param {string} followingId - User being unfollowed
 * @returns {Promise<void>}
 */
export async function unfollowUser(followerId, followingId) {
  const res = await table('follows')
    .delete()
    .eq('follower_id', followerId)
    .eq('following_id', followingId);

  if (res.error) {
    throw new RepositoryError(`unfollowUser: ${res.error.message}`, res.error);
  }
}

/**
 * Check if a user is following another user
 * @param {string} followerId - User who might be following
 * @param {string} followingId - User who might be followed
 * @returns {Promise<boolean>} True if following
 */
export async function isFollowing(followerId, followingId) {
  if (!followerId || !followingId) return false;

  const res = await table('follows')
    .select('id')
    .eq('follower_id', followerId)
    .eq('following_id', followingId)
    .single();

  return !res.error && res.data != null;
}

/**
 * Get users that a user is following
 * Supports cursor-based pagination for scalability
 *
 * @param {string} userId - User ID
 * @param {Object|number} options - Query options object or limit number (for backward compatibility)
 * @param {number} options.limit - Max results per page (default 50)
 * @param {string} options.cursor - Cursor for pagination (ISO date string)
 * @returns {Promise<{data: Array, nextCursor: string|null}|Array>} Paginated results (or array for legacy callers)
 */
export async function getFollowing(userId, options = {}) {
  if (!userId) return typeof options === 'number' ? [] : { data: [], nextCursor: null };

  // Backward compatibility: if options is a number, treat it as limit
  const isLegacyCall = typeof options === 'number';
  const limit = isLegacyCall ? options : (options.limit || 50);
  const cursor = isLegacyCall ? null : options.cursor;

  let query = table('follows')
    .select('created_at, following:user_profiles!follows_following_id_fkey(*)')
    .eq('follower_id', userId)
    .order('created_at', { ascending: false })
    .limit(limit + 1); // Fetch one extra to check if there's more

  if (cursor) {
    query = query.lt('created_at', cursor);
  }

  const res = await query;
  const data = handle(res, 'getFollowing');

  // Check if there's more data
  const hasMore = data.length > limit;
  const results = hasMore ? data.slice(0, limit) : data;
  const profiles = results.map(item => item.following).filter(Boolean);

  // Return array for legacy callers, object for new callers
  if (isLegacyCall) {
    return profiles;
  }

  return {
    data: profiles,
    nextCursor: hasMore ? results[results.length - 1]?.created_at : null,
  };
}

/**
 * Get users following a user
 * Supports cursor-based pagination for scalability
 *
 * @param {string} userId - User ID
 * @param {Object|number} options - Query options object or limit number (for backward compatibility)
 * @param {number} options.limit - Max results per page (default 50)
 * @param {string} options.cursor - Cursor for pagination (ISO date string)
 * @returns {Promise<{data: Array, nextCursor: string|null}|Array>} Paginated results (or array for legacy callers)
 */
export async function getFollowers(userId, options = {}) {
  if (!userId) return typeof options === 'number' ? [] : { data: [], nextCursor: null };

  // Backward compatibility: if options is a number, treat it as limit
  const isLegacyCall = typeof options === 'number';
  const limit = isLegacyCall ? options : (options.limit || 50);
  const cursor = isLegacyCall ? null : options.cursor;

  let query = table('follows')
    .select('created_at, follower:user_profiles!follows_follower_id_fkey(*)')
    .eq('following_id', userId)
    .order('created_at', { ascending: false })
    .limit(limit + 1); // Fetch one extra to check if there's more

  if (cursor) {
    query = query.lt('created_at', cursor);
  }

  const res = await query;
  const data = handle(res, 'getFollowers');

  // Check if there's more data
  const hasMore = data.length > limit;
  const results = hasMore ? data.slice(0, limit) : data;
  const profiles = results.map(item => item.follower).filter(Boolean);

  // Return array for legacy callers, object for new callers
  if (isLegacyCall) {
    return profiles;
  }

  return {
    data: profiles,
    nextCursor: hasMore ? results[results.length - 1]?.created_at : null,
  };
}

// =====================================================
// ACTIVITY FEED
// =====================================================

/**
 * Get activity feed for a user (own activities + followed users)
 * @param {string} userId - User ID
 * @param {Object} options - Query options
 * @returns {Promise<Array>} Activity feed items
 */
export async function getActivityFeed(userId, { limit = 50, offset = 0 } = {}) {
  if (!userId) return [];

  // Try to use the optimized RPC function first
  const res = await supabase.rpc('get_activity_feed_for_user', {
    p_user_id: userId,
    p_limit: limit,
    p_offset: offset
  });

  // Fallback if RPC doesn't exist yet
  if (res.error && res.error.code === '42883') {
    // Get list of followed users
    const followingRes = await table('follows')
      .select('following_id')
      .eq('follower_id', userId)
      .limit(200);

    const followingIds = handle(followingRes, 'getActivityFeed.following')
      .map(f => f.following_id);

    // Get activities from user + followed users
    const userIds = [userId, ...followingIds];

    const fallbackRes = await table('activity_feed')
      .select(`
        *,
        profile:user_profiles!activity_feed_user_id_fkey(username, display_name, avatar_url),
        reactions:activity_reactions(id, user_id, reaction_type)
      `)
      .in('user_id', userIds)
      .or(`user_id.eq.${userId},is_public.eq.true`)
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1);

    return handle(fallbackRes, 'getActivityFeed');
  }

  return handle(res, 'getActivityFeed');
}

/**
 * Get activities for a specific user (for their profile page)
 * @param {string} userId - User ID
 * @param {Object} options - Query options
 * @returns {Promise<Array>} Activity items
 */
export async function getUserActivities(userId, { limit = 20, offset = 0, includePrivate = false } = {}) {
  if (!userId) return [];

  let query = table('activity_feed')
    .select(`
      *,
      profile:user_profiles!activity_feed_user_id_fkey(username, display_name, avatar_url)
    `)
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .range(offset, offset + limit - 1);

  if (!includePrivate) {
    query = query.eq('is_public', true);
  }

  const res = await query;
  return handle(res, 'getUserActivities');
}

/**
 * Create a new activity
 * @param {Object} activity - Activity data
 * @returns {Promise<Object>} Created activity
 */
export async function createActivity(activity) {
  const res = await table('activity_feed')
    .insert(activity)
    .select('*')
    .single();

  if (res.error) {
    throw new RepositoryError(`createActivity: ${res.error.message}`, res.error);
  }

  return res.data;
}

/**
 * Delete an activity
 * @param {string} userId - User ID (for authorization)
 * @param {string} activityId - Activity ID
 * @returns {Promise<void>}
 */
export async function deleteActivity(userId, activityId) {
  const res = await table('activity_feed')
    .delete()
    .eq('id', activityId)
    .eq('user_id', userId);

  if (res.error) {
    throw new RepositoryError(`deleteActivity: ${res.error.message}`, res.error);
  }
}

/**
 * Delete all activities referencing a given ID (e.g., workout session or group)
 * Used for cascade cleanup when deleting workouts
 * @param {string} referenceId - The reference_id to match
 * @returns {Promise<void>}
 */
export async function deleteActivitiesByReference(referenceId) {
  if (!referenceId) return;

  const res = await table('activity_feed')
    .delete()
    .eq('reference_id', referenceId);

  // Silently handle missing table errors
  if (res.error && !isMissing(res.error)) {
    handleWarn('deleteActivitiesByReference', res.error);
  }
}

/**
 * Update activity visibility
 * @param {string} userId - User ID
 * @param {string} activityId - Activity ID
 * @param {boolean} isPublic - New visibility status
 * @returns {Promise<Object>} Updated activity
 */
export async function updateActivityVisibility(userId, activityId, isPublic) {
  const res = await table('activity_feed')
    .update({ is_public: isPublic })
    .eq('id', activityId)
    .eq('user_id', userId)
    .select()
    .single();

  return handle(res, 'updateActivityVisibility');
}

// =====================================================
// REACTIONS
// =====================================================

/**
 * Add a reaction to an activity
 * @param {string} userId - User ID
 * @param {string} activityId - Activity ID
 * @param {string} reactionType - Type of reaction (like, fire, strong, clap)
 * @returns {Promise<Object>} Created reaction
 */
export async function addReaction(userId, activityId, reactionType = 'like') {
  const res = await table('activity_reactions')
    .insert({
      user_id: userId,
      activity_id: activityId,
      reaction_type: reactionType
    })
    .select()
    .single();

  if (res.error) {
    throw new RepositoryError(`addReaction: ${res.error.message}`, res.error);
  }

  return res.data;
}

/**
 * Remove a reaction from an activity
 * @param {string} userId - User ID
 * @param {string} activityId - Activity ID
 * @returns {Promise<void>}
 */
export async function removeReaction(userId, activityId) {
  const res = await table('activity_reactions')
    .delete()
    .eq('user_id', userId)
    .eq('activity_id', activityId);

  if (res.error) {
    throw new RepositoryError(`removeReaction: ${res.error.message}`, res.error);
  }
}

/**
 * Get reactions for an activity
 * @param {string} activityId - Activity ID
 * @returns {Promise<Array>} List of reactions with user info
 */
export async function getActivityReactions(activityId) {
  if (!activityId) return [];

  const res = await table('activity_reactions')
    .select(`
      *,
      user:user_profiles!activity_reactions_user_id_fkey(username, display_name, avatar_url)
    `)
    .eq('activity_id', activityId)
    .order('created_at', { ascending: false })
    .limit(50);

  return handle(res, 'getActivityReactions');
}

// =====================================================
// COMMENTS
// =====================================================

/**
 * Get comments for an activity
 * @param {string} activityId - Activity ID
 * @returns {Promise<Array>} List of comments with user info
 */
export async function getActivityComments(activityId) {
  if (!activityId) return [];

  const res = await table('activity_comments')
    .select(`
      *,
      user:user_profiles!activity_comments_user_id_fkey(username, display_name, avatar_url)
    `)
    .eq('activity_id', activityId)
    .is('parent_comment_id', null) // Top-level comments only
    .order('created_at', { ascending: true })
    .limit(50);

  return handle(res, 'getActivityComments');
}

/**
 * Add a comment to an activity
 * @param {Object} comment - Comment data
 * @returns {Promise<Object>} Created comment with user info
 */
export async function addComment(comment) {
  const res = await table('activity_comments')
    .insert(comment)
    .select(`
      *,
      user:user_profiles!activity_comments_user_id_fkey(username, display_name, avatar_url)
    `)
    .single();

  if (res.error) {
    throw new RepositoryError(`addComment: ${res.error.message}`, res.error);
  }

  return res.data;
}

/**
 * Delete a comment
 * @param {string} userId - User ID (for authorization)
 * @param {string} commentId - Comment ID
 * @returns {Promise<void>}
 */
export async function deleteComment(userId, commentId) {
  const res = await table('activity_comments')
    .delete()
    .eq('id', commentId)
    .eq('user_id', userId);

  if (res.error) {
    throw new RepositoryError(`deleteComment: ${res.error.message}`, res.error);
  }
}

/**
 * Update a comment
 * @param {string} userId - User ID
 * @param {string} commentId - Comment ID
 * @param {string} content - New content
 * @returns {Promise<Object>} Updated comment
 */
export async function updateComment(userId, commentId, content) {
  const res = await table('activity_comments')
    .update({ content })
    .eq('id', commentId)
    .eq('user_id', userId)
    .select(`
      *,
      user:user_profiles!activity_comments_user_id_fkey(username, display_name, avatar_url)
    `)
    .single();

  return handle(res, 'updateComment');
}

// =====================================================
// PRIVACY SETTINGS
// =====================================================

/**
 * Get user privacy settings
 * @param {string} userId - User ID
 * @returns {Promise<Object|null>} Privacy settings
 */
export async function getPrivacySettings(userId) {
  if (!userId) return null;

  const res = await table('user_privacy_settings')
    .select('*')
    .eq('user_id', userId)
    .single();

  if (res.error) {
    if (isMissing(res.error) || res.error.code === 'PGRST116') {
      // Return default settings if not found
      return {
        user_id: userId,
        auto_share_workouts: true,
        auto_share_prs: true,
        auto_share_benchmarks: true,
        profile_visibility: 'public',
        allow_comments: true,
        allow_reactions: true
      };
    }
    throw new RepositoryError(`getPrivacySettings: ${res.error.message}`, res.error);
  }

  return res.data;
}

/**
 * Update user privacy settings
 * @param {string} userId - User ID
 * @param {Object} settings - Settings to update
 * @returns {Promise<Object>} Updated settings
 */
export async function updatePrivacySettings(userId, settings) {
  const res = await table('user_privacy_settings')
    .upsert({ user_id: userId, ...settings }, { onConflict: 'user_id' })
    .select()
    .single();

  if (res.error) {
    throw new RepositoryError(`updatePrivacySettings: ${res.error.message}`, res.error);
  }

  return res.data;
}

// =====================================================
// STATS & ANALYTICS
// =====================================================

/**
 * Get social stats for a user
 * @param {string} userId - User ID
 * @returns {Promise<Object>} Social stats
 */
export async function getUserSocialStats(userId) {
  if (!userId) return null;

  const profile = await getUserProfile(userId);

  if (!profile) return null;

  return {
    follower_count: profile.follower_count || 0,
    following_count: profile.following_count || 0,
    total_workouts: profile.total_workouts || 0,
    total_prs: profile.total_prs || 0
  };
}

/**
 * Get suggested users to follow (based on gym affiliation, mutual follows, etc.)
 * @param {string} userId - User ID
 * @param {number} limit - Max results
 * @returns {Promise<Array>} Suggested user profiles
 */
export async function getSuggestedUsers(userId, limit = 10) {
  if (!userId) return [];

  const currentUser = await getUserProfile(userId);
  if (!currentUser) return [];

  // Get users not already following
  const query = table('user_profiles')
    .select('*')
    .neq('user_id', userId)
    .limit(limit);

  // Prefer users from same gym
  if (currentUser.gym_affiliation) {
    query.eq('gym_affiliation', currentUser.gym_affiliation);
  }

  const res = await query;
  const allUsers = handle(res, 'getSuggestedUsers');

  // Filter out users already following
  const followingList = await getFollowing(userId);
  const followingIds = new Set(followingList.map(u => u.user_id));

  return allUsers.filter(user => !followingIds.has(user.user_id));
}
